import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
    CheckCircle,
    ArrowUpRight,
    EnvelopeSimple,
    Phone,
    Globe,
    Calendar,
    Buildings,
} from "@phosphor-icons/react";
import { PRICE_INDEX, formatCAD, PAGES, FEATURES, ASSETS } from "../data/briefCatalog";
import CalendlyEmbed from "../components/CalendlyEmbed";
import SEO from "../components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CALENDLY_URL = process.env.REACT_APP_CALENDLY_URL;

const LOGO_URL =
    "https://customer-assets.emergentagent.com/job_brand-builders-43/artifacts/6hieg182_Artboard%2010.svg";

const PAGE_LABELS = Object.fromEntries(PAGES.map((p) => [p.id, p.label]));
const FEATURE_LABELS = Object.fromEntries(FEATURES.map((f) => [f.id, f.label]));
const ASSET_LABELS = Object.fromEntries(ASSETS.map((a) => [a.id, a.label]));

export default function BriefConfirmation() {
    const { reference } = useParams();
    const [brief, setBrief] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await axios.get(
                    `${API}/brief/${encodeURIComponent(reference)}`,
                );
                if (!cancelled) setBrief(data);
            } catch (err) {
                if (!cancelled)
                    setError(
                        err?.response?.status === 404
                            ? "Ce brief est introuvable. Vérifiez votre lien."
                            : "Une erreur est survenue lors du chargement.",
                    );
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [reference]);

    if (loading) {
        return (
            <main className="min-h-screen bg-scd text-cream flex items-center justify-center">
                <span className="label-eyebrow text-cream/60">Chargement…</span>
            </main>
        );
    }
    if (error || !brief) {
        return (
            <main
                className="min-h-screen bg-scd text-cream flex flex-col items-center justify-center px-6"
                data-testid="brief-confirmation-error"
            >
                <span className="label-eyebrow text-yellow">— Erreur</span>
                <h1 className="font-display text-4xl mt-4">{error}</h1>
                <Link
                    to="/"
                    className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-yellow text-[#272727] font-semibold"
                >
                    Retour à l'accueil
                </Link>
            </main>
        );
    }

    const items = brief.selected_items
        .map((id) => PRICE_INDEX[id])
        .filter(Boolean);

    return (
        <main
            data-testid="brief-confirmation-page"
            className="bg-scd text-cream min-h-screen"
        >
            <SEO
                title={`Brief ${brief.reference} — ${brief.company}`}
                description={`Récapitulatif du brief Studio Cent Douze pour ${brief.company}. Estimation indicative : ${formatCAD(brief.estimated_total || 0)}.`}
                path={`/brief/${brief.reference}`}
                ogSlug={`brief/${brief.reference}`}
                noindex={true}
            />
            {/* Header */}
            <header className="border-b border-scd">
                <div className="px-6 md:px-12 lg:px-16 py-5 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3">
                        <img
                            src={LOGO_URL}
                            alt="Studio Cent Douze"
                            className="h-9 w-auto"
                        />
                    </Link>
                    <Link
                        to="/"
                        className="label-eyebrow text-cream/70 hover:text-yellow"
                        data-testid="brief-back-home"
                    >
                        ← Retour au site
                    </Link>
                </div>
            </header>

            {/* Hero */}
            <section className="px-6 md:px-12 lg:px-16 pt-20 pb-12 max-w-5xl mx-auto">
                <motion.div
                    initial={{ y: 30, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.6 }}
                    className="text-center"
                >
                    <CheckCircle
                        size={56}
                        weight="duotone"
                        className="text-yellow mx-auto"
                    />
                    <span
                        className="label-eyebrow text-yellow mt-6 block"
                        data-testid="brief-confirmation-reference"
                    >
                        Référence : {brief.reference}
                    </span>
                    <h1 className="font-display text-4xl md:text-6xl mt-4 leading-[0.95]">
                        Brief reçu,
                        <br />
                        <span className="text-yellow italic">
                            {brief.full_name?.split(" ")[0] || "merci"} !
                        </span>
                    </h1>
                    <p className="mt-6 max-w-xl mx-auto text-cream/75 text-lg">
                        Voici un récapitulatif de votre brief pour <strong>
                        {brief.company}</strong>. Notre équipe revient vers vous
                        avec une soumission détaillée d'ici{" "}
                        <span className="text-yellow">24 à 48&nbsp;h</span>.
                    </p>
                </motion.div>
            </section>

            {/* Body */}
            <section className="px-6 md:px-12 lg:px-16 pb-16 max-w-5xl mx-auto grid lg:grid-cols-3 gap-6">
                {/* Contact */}
                <Card title="Contact" testId="brief-conf-contact">
                    <KV icon={Buildings} label="Entreprise" value={brief.company} />
                    <KV
                        icon={EnvelopeSimple}
                        label="Email"
                        value={
                            <a
                                href={`mailto:${brief.email}`}
                                className="text-cream hover:text-yellow"
                            >
                                {brief.email}
                            </a>
                        }
                    />
                    {brief.phone && (
                        <KV icon={Phone} label="Téléphone" value={brief.phone} />
                    )}
                    {brief.current_website && (
                        <KV
                            icon={Globe}
                            label="Site actuel"
                            value={
                                <a
                                    href={brief.current_website}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-cream hover:text-yellow break-all"
                                >
                                    {brief.current_website}
                                </a>
                            }
                        />
                    )}
                    {brief.launch_date && (
                        <KV icon={Calendar} label="Lancement" value={brief.launch_date} />
                    )}
                    {brief.sector && (
                        <KV label="Secteur" value={brief.sector} />
                    )}
                </Card>

                {/* Project */}
                <Card title="Projet" testId="brief-conf-project" className="lg:col-span-2">
                    {brief.one_liner && (
                        <p className="text-cream/85 italic text-lg leading-snug">
                            "{brief.one_liner}"
                        </p>
                    )}
                    {brief.site_type && <KV label="Type de site" value={brief.site_type} />}
                    {brief.priority && <KV label="Priorité" value={brief.priority} />}
                    {brief.main_goal && <KV label="Objectif" value={brief.main_goal} />}
                    {brief.target_audience && (
                        <KV label="Public cible" value={brief.target_audience} />
                    )}
                    {brief.mood && <KV label="Ambiance" value={brief.mood} />}
                    {brief.tone && <KV label="Ton" value={brief.tone} />}
                    {brief.references && <KV label="Références" value={brief.references} />}
                    {brief.avoid && <KV label="À éviter" value={brief.avoid} />}
                    {brief.additional_info && (
                        <KV label="Notes" value={brief.additional_info} />
                    )}
                </Card>

                {/* Estimation */}
                <Card
                    title="Estimation indicative"
                    testId="brief-conf-estimation"
                    className="lg:col-span-3"
                >
                    {items.length === 0 ? (
                        <p className="text-cream/55 text-sm">
                            Aucun item sélectionné. La soumission formelle sera
                            établie après notre échange.
                        </p>
                    ) : (
                        <table className="w-full text-sm">
                            <tbody>
                                {items.map((it) => (
                                    <tr
                                        key={it.id}
                                        className="border-b border-scd last:border-none"
                                    >
                                        <td className="py-2 text-cream/85">{it.label}</td>
                                        <td className="py-2 text-cream/55 text-xs">
                                            {it.desc}
                                        </td>
                                        <td className="py-2 text-yellow text-right whitespace-nowrap">
                                            {formatCAD(it.price)}
                                            {it.suffix && (
                                                <span className="text-cream/50 text-xs">
                                                    {it.suffix}
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                    <div className="mt-6 pt-5 border-t border-scd flex items-baseline justify-between">
                        <span className="label-eyebrow text-cream/60">
                            Total estimé
                        </span>
                        <span
                            className="font-display text-4xl text-yellow"
                            data-testid="brief-conf-total"
                        >
                            {formatCAD(brief.estimated_total || 0)}
                        </span>
                    </div>
                    <p className="text-xs text-cream/50 mt-2">
                        Avant taxes (TPS + TVQ) · Dollars canadiens · Soumission
                        formelle à venir.
                    </p>

                    {/* Tags */}
                    <div className="mt-8 grid sm:grid-cols-3 gap-6">
                        {brief.pages?.length > 0 && (
                            <Tags
                                label="Pages demandées"
                                values={brief.pages}
                                dict={PAGE_LABELS}
                            />
                        )}
                        {brief.features?.length > 0 && (
                            <Tags
                                label="Fonctionnalités"
                                values={brief.features}
                                dict={FEATURE_LABELS}
                            />
                        )}
                        {brief.assets?.length > 0 && (
                            <Tags
                                label="Assets fournis"
                                values={brief.assets}
                                dict={ASSET_LABELS}
                            />
                        )}
                    </div>
                </Card>
            </section>

            {/* Calendly */}
            {CALENDLY_URL && (
                <section
                    className="px-6 md:px-12 lg:px-16 pb-24 max-w-5xl mx-auto"
                    data-testid="brief-conf-calendly"
                >
                    <div className="text-center mb-8">
                        <span className="label-eyebrow text-yellow">— Étape suivante</span>
                        <h2 className="font-display text-3xl md:text-4xl mt-3">
                            Réservez votre{" "}
                            <span className="text-yellow italic">appel découverte</span>
                            .
                        </h2>
                        <p className="text-cream/70 mt-3 max-w-xl mx-auto">
                            30 minutes pour discuter de votre projet et planifier les
                            prochaines étapes.
                        </p>
                    </div>
                    <div className="border border-scd bg-elev rounded-sm overflow-hidden">
                        <CalendlyEmbed
                            url={CALENDLY_URL}
                            height={780}
                            prefill={{
                                name: brief.full_name,
                                email: brief.email,
                                customAnswers: {
                                    a1: `Brief ${brief.reference} — ${brief.company}`,
                                },
                            }}
                        />
                    </div>
                </section>
            )}

            {/* Footer */}
            <footer className="border-t border-scd px-6 md:px-12 lg:px-16 py-10 text-center">
                <Link
                    to="/"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-scd text-cream hover:border-yellow hover:text-yellow transition-colors"
                >
                    Retour au site Studio Cent Douze
                    <ArrowUpRight size={14} weight="bold" />
                </Link>
                <p className="text-xs text-cream/40 mt-6">
                    © {new Date().getFullYear()} Studio Cent Douze · centdouze.ca
                </p>
            </footer>
        </main>
    );
}

function Card({ title, children, className = "", testId }) {
    return (
        <div
            className={`border border-scd bg-elev rounded-sm p-6 md:p-7 space-y-4 ${className}`}
            data-testid={testId}
        >
            <span className="label-eyebrow text-yellow">{title}</span>
            <div className="space-y-3">{children}</div>
        </div>
    );
}

function KV({ icon: Icon, label, value }) {
    if (!value) return null;
    return (
        <div className="flex items-start gap-3 text-sm">
            {Icon && <Icon size={14} className="text-cream/55 mt-1 shrink-0" />}
            <div className="flex-1">
                <span className="label-eyebrow text-cream/55 block">{label}</span>
                <div className="text-cream/90 mt-1 whitespace-pre-wrap">{value}</div>
            </div>
        </div>
    );
}

function Tags({ label, values, dict }) {
    return (
        <div>
            <span className="label-eyebrow text-cream/55 block mb-2">{label}</span>
            <div className="flex flex-wrap gap-1.5">
                {values.map((v) => (
                    <span
                        key={v}
                        className="px-2 py-1 rounded-sm bg-[var(--scd-yellow-tint)] text-green text-xs"
                    >
                        {dict[v] || v}
                    </span>
                ))}
            </div>
        </div>
    );
}
